import { useEffect, useRef, useState } from 'react';

export default function PhilosophySection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="philosophy"
      ref={sectionRef}
      className="py-16 sm:py-24 md:py-32 px-4 sm:px-8 bg-washi"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8 sm:gap-12 md:gap-16">
          {/* Left Side - Title */}
          <div
            className={`md:col-span-2 transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
            }`}
          >
            <div className="relative pl-5 sm:pl-6">
              <div className="absolute left-0 top-0 bottom-0 w-px bg-indigo"></div>
              <h2 className="font-sans text-3xl sm:text-4xl md:text-5xl font-bold text-sumi mb-4 sm:mb-8 tracking-wide">
                Philosophy<br />&amp; Research
              </h2>
              <h3 className="font-sans text-base sm:text-xl md:text-2xl text-sumi/90 font-medium mb-4 sm:mb-6 leading-snug">
                The Art of Perceiving<br className="sm:hidden" /> the Infinite<br className="hidden sm:block" />
                Within the Finite
              </h3>
            </div>
          </div>

          {/* Right Side - Content */}
          <div
            className={`md:col-span-3 transition-all duration-1000 delay-300 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          >
            <div className="relative">
              <div className="absolute -top-8 -right-4 sm:-right-8 w-16 sm:w-24 h-16 sm:h-24 rounded-full bg-indigo/10"></div>
              <div className="relative space-y-4 sm:space-y-6 font-sans text-sm sm:text-base leading-loose text-sumi/80">
                <p>
                  <strong className="text-sumi font-semibold">A pioneering attempt to redefine the diverse spectrum—from island minimalism to urban refinement—as a unified "Japanese Musical Culture."</strong>
                </p>
                <p>
                  The Japanese archipelago is believed to have been a crossroads where diverse cultures, carried by ocean currents, intersected. This geographical characteristic is thought to have nurtured unique musical expressions.
                </p>
                <p>
                  At our institute, while maintaining reverence for history, we reconstruct the structure of Japanese music for the contemporary era through somatic analysis based on practical knowledge. This is not mere preservation, but creative reinterpretation.
                </p>
                <p>
                  By comprehensively understanding various traditional musical currents and implementing them into contemporary art, entertainment, and education, we pass on the "insular sensibility" to the next generation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
